import { memoize } from 'lodash'
import { APIConfig } from 'types/definitions'
import { BundleServicePromiseClient } from '@taxfyle/api-internal/internal/bundle_grpc_web_pb'
import { enableDevTools } from 'utils/grpc-util'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import {
  CreateBundleRequest,
  ListBundlesRequest,
  GetWorkspacesWithBundlesRequest,
  EditBundleRequest,
  ArchiveBundleRequest,
  UnarchiveBundleRequest,
  BulkEditBundlePricesRequest,
  BulkArchiveBundlesRequest,
} from '@taxfyle/api-internal/internal/bundle_pb'

/**
 * Creates an instance of the Bundle API
 *
 * @param config
 */
export function createBundleAPI(config: APIConfig) {
  return new BundleAPI(config)
}

export class BundleAPI {
  private readonly bundleGrpcClient = createJobGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async createBundle(request: CreateBundleRequest) {
    return this.bundleGrpcClient.createBundle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listBundles(request: ListBundlesRequest) {
    return this.bundleGrpcClient.listBundles(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async getWorkspacesWithBundles(request: GetWorkspacesWithBundlesRequest) {
    return this.bundleGrpcClient.getWorkspacesWithBundles(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async editBundle(request: EditBundleRequest) {
    return this.bundleGrpcClient.editBundle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async archiveBundle(request: ArchiveBundleRequest) {
    return this.bundleGrpcClient.archiveBundle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async unarchiveBundle(request: UnarchiveBundleRequest) {
    return this.bundleGrpcClient.unarchiveBundle(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkEditBundlePrices(request: BulkEditBundlePricesRequest) {
    return this.bundleGrpcClient.bulkEditBundlePrices(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async bulkArchiveBundles(request: BulkArchiveBundlesRequest) {
    return this.bundleGrpcClient.bulkArchiveBundles(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * Creates a work dotnet client for bundles service that is memoized.
 */
const createJobGrpcClient = memoize(function createJobGrpcClient(
  baseUrl: string,
) {
  const client = new BundleServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})
